import { Listener } from './Listener';
import { TrackingHandler } from '../TrackingHandler';
import { detectFormTest } from '../../helpers/detect-form-test';
import { formatPhoneNumber } from '../../helpers/format-phone-number';
export class ServerSideGtmListener extends Listener {
    FILTERED_FIELDS = ['rct', 'captcha_solution', 'password'];
    eventCount = 0;
    formStart(eventId, formName, inputName) {
        this.sendEvent('form start', {
            name: formName,
            eventId,
            input: inputName,
        });
    }
    formNavigate(eventId, formName, formStep, navigationAction, data) {
        if (detectFormTest(data)) {
            return;
        }
        this.sendEvent('form navigate', {
            name: formName,
            eventId,
            step: formStep,
            input: navigationAction,
        });
    }
    formSubmit(eventId, formName, data) {
        if (detectFormTest(data)) {
            return;
        }
        const eventData = {
            origin_campaign_c: data.origin_campaign_c,
            origin_medium_c: data.origin_medium_c,
            origin_source_c: data.origin_source_c,
        };
        if (data.submit_id_c) {
            eventData.submit_id_c = data.submit_id_c;
        }
        this.sendEvent('form_submit_attempt', {
            name: formName,
            eventId,
            data: eventData,
            formData: this.compileSubmitData(data),
        });
    }
    formSubmitResult(eventId, formName, submitData, statusCode, responseData) {
        if (detectFormTest(submitData)) {
            return;
        }
        const eventData = {
            origin_campaign_c: submitData.origin_campaign_c,
            origin_medium_c: submitData.origin_medium_c,
            origin_source_c: submitData.origin_source_c,
        };
        if (submitData.submit_id_c) {
            eventData.submit_id_c = submitData.submit_id_c;
        }
        if (statusCode < 200 || statusCode >= 300) {
            this.sendEvent('form_submit_error', {
                name: formName,
                eventId,
                data: {
                    ...eventData,
                    error_message: responseData?.message || 'Unknown error',
                    freeTrialType: responseData?.freeTrialType || 'unknown',
                },
                formData: this.compileSubmitData(submitData),
            });
            return;
        }
        if (formName.toLowerCase().includes('consent')) {
            return;
        }
        if (!responseData) {
            return;
        }
        this.sendEvent('form_submit_success', {
            name: formName,
            eventId: eventId,
            data: {
                ...eventData,
                trackable: typeof responseData.t === 'boolean' && responseData.t,
                freeTrialType: responseData?.freeTrialType || 'unknown',
                to_id: responseData?.o || 'unknown',
            },
            formData: this.compileSubmitData(submitData),
        });
    }
    chatStart(eventId) {
    }
    chatEnd(eventId) {
    }
    pageReady(eventId) {
        this.sendEvent('page view', {
            title: document.title,
        });
    }
    setConsent(eventId, essential, functional, marketing) {
        if (window.siteSettings.disableCookieConsent) {
            return;
        }
        this.sendEvent('consent update');
    }
    static async compileEvent(eventData, additionalTrackingData = {}) {
        const trackingHandler = TrackingHandler.getInstance();
        await trackingHandler.initialized;
        const trackingData = trackingHandler.getAllTrackingData();
        const requestData = {
            event: eventData.name,
            data: eventData.data,
            user: {
                session: trackingHandler.functionalConsent ? trackingHandler.visitId : null,
                sessionTimestamp: trackingHandler.visitTimestamp,
                device: trackingHandler.functionalConsent ? trackingHandler.visitorId : null,
                deviceTimestamp: trackingHandler.visitorTimestamp,
                trackingData: Object.assign({}, trackingData, additionalTrackingData),
            },
            time: eventData.time,
            consent: trackingHandler.consent,
            consentModeV2: {
                adStorage: trackingHandler.consent.marketing,
                adUserData: trackingHandler.consent.marketing,
                adPersonalization: trackingHandler.consent.marketing,
                analyticsStorage: trackingHandler.consent.functional,
                functionalityStorage: false,
                personalizationStorage: false,
                securityStorage: false,
            },
            source: {
                type: eventData.sourceType,
                id: eventData.sourceId,
                previousId: eventData.previousSourceId,
            },
            count: eventData.count,
            countSession: eventData.sessionCount,
        };
        const requestHeaders = new Headers();
        requestHeaders.append('Accept', 'application/json');
        requestHeaders.append('Content-Type', 'application/json');
        return {
            method: 'POST',
            body: JSON.stringify(requestData),
            headers: requestHeaders,
            credentials: 'include',
        };
    }
    async sendEvent(eventName, data = {}) {
        if (!window.siteSettings.enableSSGTM) {
            return;
        }
        const trackingHandler = TrackingHandler.getInstance();
        if (trackingHandler.isInternalTest) {
            return;
        }
        this.eventCount++;
        let sessionEventCount = null;
        if (trackingHandler.functionalConsent) {
            const sessionEventCountString = sessionStorage.getItem('sgEventCount');
            sessionEventCount = sessionEventCountString ? parseInt(sessionEventCountString) + 1 : 1;
            sessionStorage.setItem('sgEventCount', sessionEventCount.toString());
        }
        const request = await ServerSideGtmListener.compileEvent({
            name: eventName,
            count: this.eventCount,
            sessionCount: sessionEventCount,
            time: new Date().getTime(),
            sourceType: 'web',
            sourceId: window.location.toString(),
            previousSourceId: document.referrer || null,
            data,
        });
        const xhr = new XMLHttpRequest();
        xhr.open(request.method || 'POST', window.endpoints.tracking);
        xhr.responseType = 'json';
        if (request.headers instanceof Headers) {
            request.headers.forEach((value, key) => {
                xhr.setRequestHeader(key, value);
            });
        }
        if (request.credentials === 'include') {
            xhr.withCredentials = true;
        }
        xhr.send(request.body);
        xhr.onreadystatechange = () => {
            if (xhr.readyState === XMLHttpRequest.DONE &&
                xhr.response &&
                typeof xhr.response === 'object' &&
                typeof xhr.response.type === 'string') {
                switch (xhr.response.type) {
                    case 'script':
                        if (typeof xhr.response.script !== 'string') {
                            return;
                        }
                        eval(xhr.response.script);
                }
            }
        };
    }
    compileSubmitData(data) {
        const result = {};
        for (const key of Object.keys(data)) {
            if (this.FILTERED_FIELDS.includes(key)) {
                result[key] = '***';
                continue;
            }
            result[key] = key.toLowerCase().includes('phone') ? formatPhoneNumber(data[key]) : data[key];
        }
        return result;
    }
}
